import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress, Box, Alert, Typography, Button, Paper } from '@mui/material';
import { API_CONFIG } from '../../../lib/config/api';

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, token, user } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkAuth = () => {
      try {
        const storedToken = localStorage.getItem('token');

        if (storedToken && (isAuthenticated || token)) {
          setIsLoading(false);
          setError(null);
        } else if (!storedToken) {
          setIsLoading(false);
          setError('No authentication token found');
        } else {
          // Wait a bit longer if we have a token but auth state isn't ready
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            checkAuth();
          }, 500);
        }
      } catch (err) {
        setError(`Authentication error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    checkAuth();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated, token, user]);

  // More comprehensive debug log for authentication state
  useEffect(() => {
    // console.log({
    //   isAuthenticated,
    //   hasToken: !!token,
    //   tokenFirstChars: token ? `${token.substring(0, 15)}...` : 'none',
    //   localStorageToken: localStorage.getItem('token') ? `${localStorage.getItem('token')?.substring(0, 15)}...` : 'none',
    //   hasUser: !!user,
    //   userData: user ? {
    //     id: user.id,
    //     name: user.name,
    //     email: user.email,
    //     role: user.role
    //   } : 'none',
    //   path: location.pathname,
    //   isLoading,
    //   error
    // });
  }, [isAuthenticated, token, user, location, isLoading, error]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Show detailed error instead of immediately redirecting
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
        <Paper elevation={3} sx={{ p: 3, maxWidth: 600, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Authentication Failed: {error}
          </Alert>
          <Typography variant="body1" gutterBottom>
            You need to be logged in to access this page. Please login again.
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                localStorage.removeItem('token');
                window.location.href = '/login';
              }}
            >
              Go to Login
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowDebugInfo(prev => !prev)}
            >
              {showDebugInfo ? 'Hide Debug Info' : 'Show Debug Info'}
            </Button>
          </Box>

          {showDebugInfo && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Debug Information:</Typography>
              <Box component="pre" sx={{ overflowX: 'auto', fontSize: '0.875rem' }}>
                {JSON.stringify({
                  isAuthenticated,
                  hasToken: !!token,
                  hasStoredToken: !!localStorage.getItem('token'),
                  hasUser: !!user,
                  currentPath: location.pathname,
                  apiBaseUrl: API_CONFIG.BASE_URL,
                  nodeEnv: process.env.NODE_ENV,
                }, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  // Check if route should be protected
  const shouldRedirect = !isAuthenticated || !token;

  if (shouldRedirect) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }


  // Redirect to dashboard if user is accessing the root protected route
  if (location.pathname === '/' && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
